import { endsWith, startsWith } from 'ramda';
import { FileWithPath } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { UploadFileSignedUrlMutationVariables } from '../../graphql/generated';

export type EnhancedFile = {
  id: string;
  file: FileWithPath;
  path: string;
};

export const VENDIA_CHAR_LIMIT = 1024;

// Thumbnail cache files for macOS and Windows
const FILES_TO_IGNORE = [
  '.DS_Store', // macOs
  'Thumbs.db', // Windows
];

export const formatFileSize = (bytes: number): string => {
  const units = ['bytes', 'KB', 'MB', 'GB'];
  let i = 0;
  let fileSize = bytes;

  while (fileSize >= 1024 && ++i) {
    fileSize = fileSize / 1024;
  }

  const fractionDigits = fileSize < 10 && i > 0 ? 2 : 0;

  return fileSize.toFixed(fractionDigits) + ' ' + units[i];
};

/**
 * @name createFile
 * @description
 * Takes file content, file name and file type and Returns a new File object
 */
export const createFile = (
  fileBits: BlobPart,
  filename: string,
  fileType: string
): File =>
  new File([fileBits], filename, {
    type: fileType,
  });

/**
 * @name formatFilePath
 * @description
 * Removes leading and trailing '/', otherwise returns filePath
 */
export const formatFilePath = (filePath: string): string => {
  let path = filePath;

  if (startsWith('/', path)) {
    path = path.slice(1);
  }

  if (endsWith('/', path)) {
    path = path.slice(0, -1);
  }

  return path;
};

/**
 * @name isStorageObjectValid
 * @description
 * Checks whether the storageObject name is within the permitted character limit.
 * Vendia impose a 1024 character limit
 */
export const isStorageObjectValid = (name: string): boolean => {
  const fileExceedsCharLength = name.length > VENDIA_CHAR_LIMIT;

  if (fileExceedsCharLength) return false;

  return true;
};

const enhanceFile = (file: FileWithPath): EnhancedFile => {
  let path = file.path ? formatFilePath(file.path) : file.name;
  
  // Remove ./ prefix if present
  if (path.startsWith('./')) {
    path = path.slice(2);
  }
  
  return {
    id: uuidv4(),
    file,
    path,
  };
};

/**
 * @name enhanceFiles
 * @description
 * Takes a list of FileWithPath and wraps each file in an object with
 * id, file (original input file) and a formatted path.
 */
export const enhanceFiles = (files: FileWithPath[]): EnhancedFile[] =>
  files.map(enhanceFile);

export const getUploadFileSignedUrlMutationInput = (
  cedant: string,
  filename: string,
  contentType: string
): UploadFileSignedUrlMutationVariables => {
  return {
    cedant,
    input: {
      filename,
      contentType,
    },
  };
};

/**
 * @name removeIgnoredFiles
 * @description
 * Takes a list of FileWithPath and filters out any occurrences
 * of items within FILES_TO_IGNORE (such as .DS_Store, Thumbs.db)
 */
export const removeIgnoredFiles = (files: FileWithPath[]) =>
  files.filter((file) => !FILES_TO_IGNORE.includes(file.name));
